import React, { useEffect, useState } from 'react';
import toast, { Toaster } from "react-hot-toast";
import { useDispatch, useSelector } from "react-redux";
import { Outlet, useLocation, useNavigate } from "react-router-dom";

import AppStepper from "../../components/AppStepper/AppStepper";
import Loader from "../../components/Loader/Loader";
import ModalCreatedOrder from "../../components/ModalCreatedOrder/ModalCreatedOrder";
import MyOrder from "../../components/MyOrder/MyOrder";
import { createOrderStep1Validator } from "../../constants/createOrderStep1Validator";
import { createOrderStep3Validator } from "../../constants/createOrderStep3Validator";
import { MAIN_ROUTES } from "../../routing/main_routes";
import { cleaningRequestService } from "../../services/cleaningRequest.service";
import { UIActions } from "../../store/slices/UISlice";
import { additionalServicesActions } from "../../store/slices/additionalServicesSlice";
import { cleaningRequestsActions } from "../../store/slices/cleaningRequestsSlice";
import { qrCodeActions } from "../../store/slices/qrCodeSlice";
import { servicePackagesActions } from "../../store/slices/servicePackagesSlice";
import { stepperInfoActions } from "../../store/slices/stepperInfoSlice";
import { buildCleaningAddress } from "../../utils/buildCleaningAddress";
import { NOT_SERVICEABLE_MESSAGE } from "../../utils/serviceability";
import { urlsToFiles } from "../../utils/urlsToFiles";
import styles from './CreateOrderLayout.module.scss';

const CreateOrderLayout = ({setCurrentStep}) => {
    const [isModalCreatedOrderOpen, setIsModalCreatedOrderOpen] = useState(false);
    const [error, setError] = useState("");

    const navigate = useNavigate();

    const {pathname, state} = useLocation();
    const pathArray = pathname.split('/');
    const currentStepFromUrl = pathArray[pathArray.length - 1];

    const dispatch = useDispatch();
    const {status: status1} = useSelector(state => state.servicePackages);
    const {status: status2} = useSelector(state => state.cleaningRequests);
    const {isLoading, isSessionEnded} = useSelector(state => state.UI);

    const {photos, photosSyncing} = useSelector(state => state.qrCode);

    const currentStep = JSON.parse(localStorage.getItem('currentStep')) || 1;

    const stepperInfo = useSelector(state => state.stepperInfo);

    const {
        selectedCity: city,
        street,
        buildingNumber,
        entrance,
        apartment,
        isServiceable,
        serviceabilityError,
        selectedFiles,
        service_package,
        countPanoramicWindows: pano_window_count,
        countOrdinaryWindows: reg_window_count,
        selectedDate,
        selectedTime,
        typeBuilding,
        type_cleaning,
        squareApartment: area,
        countRooms: room_count,
        countBathrooms: wc_count,
        request_price,
        promo_code,
        finalPrice,
        accessToApartment: present_at_home,
        locationAccessToApartment: present_at_home_desc,
        services,
        request_datetime,
        request_files,
        comment,
    } = useSelector(state => state.stepperInfo);

    const turnReceivedImgUrlsToFiles = (photoUrls) => urlsToFiles(photoUrls);

    const titles = {
        'step-1': 'Де ми будемо прибирати ваше місце?',
        'step-2': 'Вибір пакету',
        'step-3': 'Деталі послуг',
        'step-4': 'Дата та час замовлення',
        'step-5': 'Інформація про вас',
    }

    const isUser = JSON.parse(localStorage.getItem('token'))?.access;

    let labelArray;
    if (isUser) {
        labelArray = ['Локація', 'Вибір пакету', 'Деталі послуг', 'Дата та час'];
    } else {
        labelArray = ['Локація', 'Вибір пакету', 'Деталі послуг', 'Дата та час', 'Особисті дані'];
    }

    const handleCreateFormData = (photos, cleaning_request_data, qrPhotos) => {
        let formData = new FormData();

        if (photos?.length > 0) {
            for (const photo of photos) {
                formData.append('photos_data', photo);
            }
        }

        if (qrPhotos?.length > 0) {
            for (const photo of qrPhotos) {
                formData.append('photos_data', photo);
            }
        }

        formData.append('cleaning_request_related_data', JSON.stringify(cleaning_request_data));

        return formData;
    };

    const updateStep = (step) => {
        let isValid =  true;
        if (step === 2) {
            isValid = createOrderStep1Validator(city, street, buildingNumber);

            // Hard serviceability gate: do not let the user proceed to step-2
            // with a non-serviceable address (wrong city or outside radius).
            if (isValid && !isServiceable) {
                toast.error(serviceabilityError || NOT_SERVICEABLE_MESSAGE, {id: "serviceability"});
                return;
            }
        }

        if (step === 4) {
            const isCustomPackage = service_package.name === "Індивідуальний пакет";
            isValid = createOrderStep3Validator(area, pano_window_count, reg_window_count, request_price, finalPrice, room_count, wc_count, isCustomPackage, type_cleaning);
        }

        if (step && isValid) {
            // setCurrentStep(step);
            // console.log(step, currentStep)
            setCurrentStep(step);
            localStorage.setItem('currentStep', JSON.stringify(step));
            // dispatch(stepperInfoActions.setCurrentStep(step));
            navigate(`/create-order/step-${step}`);
        }
    }

    const step1Complete = (!!city && (!!street && !!buildingNumber));
    const step2Complete = (!!service_package.name);
    const step3Complete = (() => {
        if (service_package.name === 'Індивідуальний пакет') {
            if (type_cleaning === "Генеральне") {
                return (((room_count || wc_count) && (request_price >= finalPrice)
                    && !!typeBuilding && (!!area && area > 0)));
            } else if (type_cleaning === "Миття вікон") {
                return (((pano_window_count && pano_window_count > 0) ||
                        (reg_window_count && reg_window_count > 0))) &&
                    (request_price >= finalPrice) && !!typeBuilding && (!!area > 0);
            }
            return (request_price >= finalPrice) && !!typeBuilding && !!area;
        } else {
            return (((pano_window_count && pano_window_count > 0)
                || (reg_window_count && reg_window_count > 0)) && (request_price >= finalPrice));
        }
    })();
    const step4Complete = labelArray.length === 4 || (selectedDate && selectedTime && present_at_home &&
        (present_at_home !== 'Я дам код до дверей' && present_at_home !== 'Залишу ключі' || present_at_home_desc));

    const canProceedToNextStep = () => {
        if (currentStep === 1) {
            return step1Complete;
        } else if (currentStep === 2) {
            return step2Complete;
        } else if (currentStep === 3) {
            return step3Complete;
        } else if (currentStep === 4) {
            return step4Complete;
        }
    };

    const handleCreateOrder = async () => {
        // Defensive: never submit while QR photos are still syncing/loading.
        if (photosSyncing) {
            toast.error("Зачекайте, фото ще завантажуються…", {id: "create-order"});
            return;
        }
        dispatch(cleaningRequestsActions.setStatus('loading'));

        updateStep(null);

        const servicesIds = services?.map((service) => service?.id);

        const cleaning_request = {
            ...buildCleaningAddress(stepperInfo),
            cleaning_type: type_cleaning || 'Генеральне',
            area,
            services: servicesIds,
            present_at_home,
            present_at_home_desc: present_at_home_desc || null,
            room_count: room_count || null,
            wc_count: wc_count || null,
            pano_window_count: pano_window_count || null,
            reg_window_count: reg_window_count || null,
            comment: comment || null,
            request_price,
            promo_code: promo_code || null,
            request_datetime,
            service_package: service_package.id
        };


        const files = await turnReceivedImgUrlsToFiles(photos); //photos from qr code
        const formData = handleCreateFormData(request_files, cleaning_request, files);

        toast.promise(
            cleaningRequestService.create(formData),
            {
                loading: "Створення замовлення...",
                success: ({data}) => {
                    setIsModalCreatedOrderOpen(prev => !prev);
                    return "Ваше замовлення було успішно створено!";
                },
                error: (error) => {
                    // setTimeout(() => {
                    //     navigate(MAIN_ROUTES.CREATE_ORDER);
                    // }, 2000);
                    return `На жаль, не вдалось створити замовлення. ${JSON.stringify(error?.response?.data)}. Спробуйте, будь ласка, ще раз!`
                },
            }
        , {id: "create-order"}).catch((error) => {
            console.error("Error updating data:", error);
            // navigate(MAIN_ROUTES.CREATE_ORDER);
            navigate(MAIN_ROUTES.CREATE_ORDER);
            toast.error(`На жаль, не вдалось створити замовлення. ${JSON.stringify(error?.response?.data)}. Спробуйте, будь ласка, ще раз!`, {id: "create-order"})
        }).finally(() => {
            dispatch(stepperInfoActions.clearAllData());
            dispatch(qrCodeActions.clearAllData());
            localStorage.setItem('currentStep', JSON.stringify(1));
            setCurrentStep(1);
            dispatch(cleaningRequestsActions.setStatus('success'));
        })
    }

    useEffect(() => {
        const handleBeforeUnload = () => {
            dispatch(stepperInfoActions.setRequestFiles([]));
            dispatch(stepperInfoActions.setSelectedFiles([]));
        };

        window.addEventListener('load', handleBeforeUnload);

        return () => {
            window.removeEventListener('load', handleBeforeUnload);
            toast.remove("create-order");
        };
    }, []);

    useEffect(() => {
        const getAllServicePackages = async () => {
            try {
                await dispatch(servicePackagesActions.getAll());
            } catch (error) {
                console.error(error);
            }
        }

        getAllServicePackages();
    }, []);

    useEffect(() => {
        const getAdditionalServices = async () => {
            // by default, type_cleaning: Генеральне
            if (!type_cleaning) {
                await dispatch(additionalServicesActions.getByServiceType({service_type: 'Генеральне'}));
            } else {
                await dispatch(additionalServicesActions.getByServiceType({service_type: type_cleaning}));
            }
        }

        getAdditionalServices();
    }, [type_cleaning]);

    useEffect(() => {
        return () => {
            dispatch(stepperInfoActions.clearAllData());
            setCurrentStep(1);
            localStorage.setItem('currentStep', JSON.stringify(1));
            dispatch(qrCodeActions.clearAllData());
        }
    }, []);

    // NOTE: QR-session generation lives entirely in <AddPhoto/> (step-3), which
    // creates it exactly once per mount (guarded by generatingRef + !session_id).
    // The layout must NOT also generate here: this effect previously re-fired on
    // every change to `photos`, and the AddPhoto poll dispatches setPhotos([]) for
    // empty results (a fresh [] reference each 3s tick), which retriggered this
    // effect into a tight /generate-qr/ POST loop until the throttle returned 429.

    useEffect(() => {
        if (isSessionEnded) {
            dispatch(UIActions.setIsSessionEnded(false));
            alert('Сесія завантаження фото за допомогою QR коду закінчилась! Спробуйте ще раз!');
        }
    }, [isSessionEnded]);

    useEffect(() => {
        if (error) {
            toast.error(error, {id: "error"});
            setError(null);
        }
    }, [error])

    useEffect(() => {
        return () => {
            toast.remove("error")
        }
    }, []);

    if ((status1 === 'loading') || (status2 === 'loading') || isLoading) {
        return <Loader/>
    }

    return (
        <div className={styles.createOrderLayout}>
            <div className={styles.createOrderLayout_wrapper}>
                <div className={styles.createOrderLayout_wrapper_filling}>
                    <h1 className={styles.createOrderLayout_wrapper_filling_title}>{titles[currentStepFromUrl]}</h1>

                    <AppStepper
                        updateStep={updateStep}
                        currentStep={currentStep}
                        labelArray={labelArray}
                    />

                    <Outlet context={{error, setError}}/>

                    <div className={styles.createOrderLayout_wrapper_filling_buttons}>
                        <button
                            className={`${styles.prev} ${currentStep === 1 ? styles.hide : ''}`}
                            disabled={currentStep === 1}
                            onClick={() => updateStep(currentStep - 1)}
                        >Назад
                        </button>

                        <button
                            className={`
                        ${styles.next}
                        ${(currentStep === labelArray.length && labelArray.length !== 4) ? styles.hide : ''}
                        ${(currentStep === labelArray.length && isUser) ? styles.create : ''}
                        `}
                            // Block advancing/submitting while QR photos are
                            // still syncing from the phone, so we never POST an
                            // order with half-uploaded / not-yet-fetched photos.
                            disabled={photosSyncing}
                            onClick={() => (isUser && currentStep === 4) ? handleCreateOrder() : updateStep((currentStep + 1))}
                        >
                        </button>
                    </div>
                </div>

                <MyOrder/>
                {isModalCreatedOrderOpen && <ModalCreatedOrder/>}

                {/* to appear hot toast notification */}
                <Toaster/>
            </div>
        </div>
    );
};


// ${(user && currentStep === labelArray.length) ? styles.edit : ''}


export default CreateOrderLayout;
